import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";

import { useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

import {
    Card,
    CardProps
} from "../../../../libraries/design/components/card/src/card";
import { getIsGwp10027NonprofitPricingPage } from "../feature-gates.ts";

const resourcesContainerCss = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center"
});

const headerCss = css({
    fontSize: "32px",
    lineHeight: "38px",
    fontWeight: 800,
    margin: "0 0 14px 0",
    textAlign: "center",
    [media.above.sm]: {
        fontSize: "48px",
        lineHeight: "56px",
        margin: "0 0 16px 0"
    }
});

const subtitleCss = css({
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 300,
    margin: "0 auto 20px",
    textAlign: "center",
    [media.only.sm]: {
        fontSize: "20px",
        lineHeight: "28px",
        maxWidth: "768px",
        margin: "0 auto 35px"
    },
    [media.above.md]: {
        fontSize: "28px",
        lineHeight: "40px",
        maxWidth: "100%"
    }
});

const cardBlockCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".row-layout": {
        padding: "0",
        width: "100%"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["img"]: {
        aspectRatio: "auto !important"
    }
});

export const ResourcesSection = () => {
    const intl = useIntl();

    const teamCardProps: CardProps = {
        title: intl.formatMessage({
            id: "NonprofitPage.resources.team.title",
            defaultMessage: "Team up with us"
        }),
        titleAs: "h3",
        body: intl.formatMessage({
            id: "NonprofitPage.resources.team.body",
            defaultMessage:
                "Get free, customized app and teamwork coaching sessions with Atlassian experts. No question is too big or too small."
        }),
        image: {
            image: {
                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/7g2k6ru6bw83f33121usy12j03td6p88.webp",
                alt: "Partners for Nonprofits"
            }
        },
        callToAction: {
            href: useLocalizedHref("/teams/nonprofits/support-programs"),
            text: intl.formatMessage({
                id: "NonprofitPage.resources.team.link",
                defaultMessage: "Learn more"
            })
        }
    };

    const isGwp10027 = getIsGwp10027NonprofitPricingPage();

    const successCardProps: CardProps = {
        title: intl.formatMessage({
            id: "NonprofitPage.resources.success.title.gwp7869",
            defaultMessage: "Social impact team success stories"
        }),
        titleAs: "h3",
        body: intl.formatMessage({
            id: "NonprofitPage.resources.success.body",
            defaultMessage:
                "Get inspired by teams like yours driving impact with Atlassian apps."
        }),
        image: {
            image: {
                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/6kp84so85h055h6o517q02jx1qp42p78.webp",
                alt: "Partners for Nonprofits"
            }
        },
        callToAction: {
            href: useLocalizedHref("/teams/nonprofits/resources"),
            text: intl.formatMessage({
                id: "NonprofitPage.resources.success.link",
                defaultMessage: "See all use cases"
            })
        }
    };

    return (
        <div css={resourcesContainerCss}>
            <h2 css={headerCss}>
                <FormattedMessage
                    id="NonprofitPage.resources.header.v1"
                    defaultMessage="Explore resources"
                />
            </h2>
            <h3 css={subtitleCss}>
                {isGwp10027 && (
                    <FormattedMessage
                        id="NonprofitPage.resources.subtitle.gwp10027"
                        defaultMessage="While you wait for your Social Impact License to be approved…"
                    />
                )}

                {!isGwp10027 && (
                    <FormattedMessage
                        id="NonprofitPage.resources.subtitle"
                        defaultMessage="While you wait for your Community License to be approved…"
                    />
                )}
            </h3>
            <div css={cardBlockCss}>
                <Row css={cardBlockCss} layout="6-6">
                    <Card {...teamCardProps} />
                    <Card {...successCardProps} />
                </Row>
            </div>
        </div>
    );
};
ResourcesSection.displayName = "ResourcesSection";
